import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import ConfirmDialog from '../../components/ConfirmDialog';
import TableToggle from '../payment/TableToggle';
import SmsConfigEditModal from './SmsConfigEditModal';
import {
  buildFieldLabelMap,
  buildProviderNameMap,
  formatSmsFieldValue,
  formToSmsPayload,
  getAvailableProviderOptions,
  getSmsProviderTitle,
  rowToSmsForm,
} from './smsManageConstants';

export default function SmsManagePage() {
  const [records, setRecords] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  const providerNameMap = useMemo(
    () => buildProviderNameMap(contentTypes),
    [contentTypes]
  );
  const fieldLabels = useMemo(
    () => buildFieldLabelMap(contentTypes),
    [contentTypes]
  );
  const addOptions = useMemo(
    () => getAvailableProviderOptions(
      contentTypes,
      records.map((row) => row.providerKey)
    ),
    [contentTypes, records]
  );

  async function loadContentTypes() {
    try {
      const data = await adminApi.smsContentTypes();
      setContentTypes(Array.isArray(data) ? data : []);
    } catch {
      setContentTypes([]);
    }
  }

  async function loadRecords() {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listSmsConfigs();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadContentTypes();
    loadRecords();
  }, []);

  function openCreate() {
    if (addOptions.length === 0) {
      setError('所有短信服務商皆已配置');
      return;
    }
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
    setError('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload, isEdit) {
    setSubmitting(true);
    setError('');
    try {
      if (isEdit) {
        await adminApi.updateSmsConfig(payload);
        setMessage('修改成功');
      } else {
        await adminApi.addSmsConfig(payload);
        setMessage('新增成功');
      }
      setEditOpen(false);
      await loadRecords();
    } catch (err) {
      setError(err.message || '儲存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleEnabled(row, enabled) {
    setTogglingKey(row.providerKey);
    setError('');
    try {
      const payload = formToSmsPayload(rowToSmsForm(row, fieldLabels));
      payload.enabled = enabled;
      await adminApi.updateSmsConfig(payload);
      setMessage(enabled ? '已啟用' : '已停用');
      await loadRecords();
    } catch (err) {
      setError(err.message || '更新狀態失敗');
    } finally {
      setTogglingKey(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget?.providerKey) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.deleteSmsConfig(deleteTarget.providerKey);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords();
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>短信管理</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-secondary" onClick={loadRecords}>
            重整
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreate}
            disabled={addOptions.length === 0}
          >
            新增
          </button>
        </div>
      </div>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      {loading ? (
        <p className="table-empty">載入中…</p>
      ) : records.length === 0 ? (
        <p className="table-empty">尚無短信配置，請點擊「新增」</p>
      ) : (
        <div className="sms-config-grid">
          {records.map((row) => {
            const labels = fieldLabels[row.providerKey] || {};
            const content = row.content || {};
            return (
              <article key={row.providerKey} className="sms-config-card">
                <div className="sms-config-card__header">
                  <div className="sms-config-card__title">
                    <h3>{getSmsProviderTitle(row, providerNameMap)}</h3>
                    <span className={`sms-config-card__status ${row.enabled ? 'is-on' : 'is-off'}`}>
                      {row.enabled ? '開' : '關'}
                    </span>
                  </div>
                  <TableToggle
                    checked={Boolean(row.enabled)}
                    disabled={togglingKey === row.providerKey}
                    ariaLabel="啟用狀態"
                    onChange={(checked) => handleToggleEnabled(row, checked)}
                  />
                </div>
                <dl className="sms-config-card__fields">
                  {Object.entries(labels).map(([key, label]) => (
                    <div key={key} className="sms-config-card__field">
                      <dt>{label || key}</dt>
                      <dd>{formatSmsFieldValue(key, content[key])}</dd>
                    </div>
                  ))}
                  <div className="sms-config-card__field">
                    <dt>更新時間</dt>
                    <dd>{formatDateTime(row.updatedDate)}</dd>
                  </div>
                </dl>
                <div className="sms-config-card__actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEdit(row)}>
                    修改
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => setDeleteTarget(row)}
                  >
                    刪除
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <SmsConfigEditModal
        open={editOpen}
        row={editRow}
        fieldLabels={fieldLabels}
        providerNameMap={providerNameMap}
        providerOptions={editRow ? [{ providerKey: editRow.providerKey, providerName: providerNameMap[editRow.providerKey], content: fieldLabels[editRow.providerKey] }] : addOptions}
        onClose={() => setEditOpen(false)}
        onSubmit={handleSave}
        submitting={submitting}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除短信配置"
        message={`確定刪除「${getSmsProviderTitle(deleteTarget, providerNameMap)}」？`}
        confirmLabel={deleting ? '刪除中…' : '刪除'}
        confirmDisabled={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
